import { useForm, FieldPath } from 'react-hook-form'
import { z } from 'zod'
import { zodResolver } from '@hookform/resolvers/zod'
import { TextInput } from '../../FormUtils/TextInput'
import React, { useMemo, useState } from 'react'
import Select from '../../FormUtils/Select'
import apiService from '../../../services/api'
import { Modal } from 'components/UI/Modal/Modal'
import { toast } from 'react-toastify'
import { featureFlagService } from 'utils/featureFlagService'
import { ChevronDown, ChevronUp } from 'lucide-react'
import dialogConfirm from 'components/dialogConfirm'
import { FbLink } from 'components/FbUI/FbLink'
import { isNonRecurringProduct, Product } from 'models/product'
import {
  checkDoorSelectionRules,
  selectDoorsStepFormSchema,
  SelectDoorsStepWithControl,
} from '../CreateDealModal/SelectDoorsStep'
import { SingleVariantPicker } from 'components/UI/SingleVariantPicker'
import type { SelectedVariant } from 'components/UI/ProductSearchModal'
import { useQueryClient } from '@tanstack/react-query'
import { Spinner } from 'react-bootstrap'
import { DealDetail } from 'models/deals'

interface EditDealModalProps {
  show: boolean
  onClose: () => void
  dealId?: number
  dealsTableQueryKey: string[]
  /**
   * When true, uses invalidateQueries instead of refetchQueries after save.
   * Used by the reports-based deals table (V2).
   */
  useInvalidateOnSave?: boolean
}

const editDealSchema = z
  .object({
    name: z
      .union([
        z.string().max(100, 'Deal name must be 100 characters or less'),
        z.null(),
        z.undefined(),
      ])
      .optional()
      .nullable(),
    monthly_volume_override: z.union([
      z.number().min(0).optional().nullable(),
      z.literal(''),
    ]),
    monthly_revenue_override: z.union([
      z.number().min(0).optional().nullable(),
      z.literal(''),
    ]),
    close_date_override: z.string().optional().nullable(),
    account_owner: z.number().optional().nullable(),
    sales_stage: z.number().optional().nullable(),
    product_variant_id: z.number().optional().nullable(),
    notes: z.string().optional().nullable(),
    attachments: z.array(z.any()).optional().nullable(),
    origin_campaign_id: z.number().optional().nullable(),
    product_id: z.number(),
  })
  .merge(selectDoorsStepFormSchema)
  .superRefine((v, ctx) => {
    checkDoorSelectionRules(v, ctx)
  })
type EditDealFormValues = z.infer<typeof editDealSchema>

// ---------------------------------------------------------------------------
// Shell: fetches all data, shows spinner until ready, then renders form content
// ---------------------------------------------------------------------------

export function EditDealModal(props: EditDealModalProps) {
  const api = apiService()

  // Fetch fresh deal data when modal opens
  const { data: deal, isLoading: isDealLoading } = api.useGetDeal(
    props.dealId,
    props.show,
    { noCache: true }
  )

  const { data: stageOptions, isLoading: isStageOptionsLoading } =
    api.useGetSalesStageOptions()
  const { data: userOptions, isLoading: isUserOptionsLoading } =
    api.useGetUserOptions({ onlyActive: true })
  const { data: productOptions, isLoading: isProductOptionsLoading } =
    api.useGetProductsOptions()

  const { data: campaignOptions, isLoading: isCampaignOptionsLoading } =
    api.useGetCampaignsOptions({
      typeOrTypeId: deal?.contact_company?.sub_category ?? undefined,
      requireFilter: true,
    })

  const { data: { count: companyTotalDoors } = { count: 0 } } =
    api.useGetDoorsForSelectionCount(deal?.contact_company?.id ?? 0)

  const { data: products, isLoading: isProductsLoading } = api.useGetProducts()

  // Fetch door IDs via React Query (only when deal uses select_locations)
  const shouldFetchDoorIds =
    props.show && !!deal && deal.apply_to === 'select_locations'
  const { data: doorIdsData, isLoading: isDoorIdsLoading } =
    api.useGetSelectedDoorIdsForDeal(deal?.id, shouldFetchDoorIds, {
      noCache: true,
    })

  const isReady = useMemo(() => {
    if (isDealLoading) return false
    if (
      isStageOptionsLoading ||
      isUserOptionsLoading ||
      isProductOptionsLoading ||
      isCampaignOptionsLoading ||
      isProductsLoading
    )
      return false
    if (shouldFetchDoorIds && isDoorIdsLoading) return false
    if (!deal) return false
    return true
  }, [
    isDealLoading,
    isStageOptionsLoading,
    isUserOptionsLoading,
    isProductOptionsLoading,
    isCampaignOptionsLoading,
    isProductsLoading,
    shouldFetchDoorIds,
    isDoorIdsLoading,
    deal,
  ])

  // Compute initial door IDs for the form
  const initialDoorIds = useMemo(() => {
    if (deal?.apply_to === 'select_locations') {
      return doorIdsData?.ids ?? []
    }
    return []
  }, [deal?.apply_to, doorIdsData?.ids])

  // Compute initial selected variant from deal + products
  const initialSelectedVariant = useMemo<SelectedVariant | null>(() => {
    if (!deal || !products) return null
    const variantId = deal.product_variant_id
    if (!variantId) return null

    const product = products.find((p) => p.id === deal.product_id)
    if (!product?.variants) return null

    const variant = product.variants.find((v) => v.id === variantId)
    if (!variant) return null

    return {
      id: variant.id!,
      name: variant.name,
      format_pack_size: variant.format_pack_size || null,
      sku: variant.sku || '',
      gtin: variant.gtin || null,
      product_id: product.id,
      product_name: product.name,
      distributor_codes: variant.distributor_codes || [],
      avg_price: variant.avg_price || null,
    }
  }, [deal, products])

  // Don't render anything when modal is closed
  if (!props.show) return null

  // Show loading modal until all data is ready
  if (!isReady) {
    return (
      <Modal
        open={true}
        onOpenChange={(open) => !open && props.onClose()}
        title="Edit Deal"
        size="lg"
        onCancel={props.onClose}
      >
        <div className="flex items-center justify-center py-12">
          <div className="flex flex-col items-center gap-2">
            <Spinner animation="border" />
            <span className="text-gray-600 text-sm">Loading deal data...</span>
          </div>
        </div>
      </Modal>
    )
  }

  // All data ready - render form content (owns its own Modal)
  return (
    <EditDealFormContent
      key={deal!.id}
      deal={deal!}
      initialDoorIds={initialDoorIds}
      initialSelectedVariant={initialSelectedVariant}
      products={products!}
      stageOptions={stageOptions}
      userOptions={userOptions}
      productOptions={productOptions}
      campaignOptions={campaignOptions}
      companyTotalDoors={companyTotalDoors}
      dealsTableQueryKey={props.dealsTableQueryKey}
      useInvalidateOnSave={props.useInvalidateOnSave}
      onClose={props.onClose}
    />
  )
}

// ---------------------------------------------------------------------------
// Form content: receives ready data, initializes form directly, no sync effects
// ---------------------------------------------------------------------------

interface EditDealFormContentProps {
  deal: DealDetail
  initialDoorIds: number[]
  initialSelectedVariant: SelectedVariant | null
  products: Product[]
  stageOptions: any
  userOptions: any
  productOptions: any
  campaignOptions: any
  companyTotalDoors: number
  dealsTableQueryKey: string[]
  useInvalidateOnSave?: boolean
  onClose: () => void
}

function EditDealFormContent({
  deal,
  initialDoorIds,
  initialSelectedVariant,
  products,
  stageOptions,
  userOptions,
  productOptions,
  campaignOptions,
  companyTotalDoors,
  dealsTableQueryKey,
  useInvalidateOnSave,
  onClose,
}: EditDealFormContentProps) {
  const queryClient = useQueryClient()
  const featureFlag = featureFlagService()
  const api = apiService()

  const [overridesExpanded, setOverridesExpanded] = useState(false)
  const [selectedVariant, setSelectedVariant] =
    useState<SelectedVariant | null>(initialSelectedVariant)
  const [isDeleteConfirmOpen, setIsDeleteConfirmOpen] = useState(false)
  const [isDeleting, setIsDeleting] = useState(false)

  // Form initialized directly with all ready data - no sync useEffects needed
  const form = useForm<EditDealFormValues>({
    resolver: zodResolver(editDealSchema),
    mode: 'onChange',
    reValidateMode: 'onChange',
    criteriaMode: 'all',
    defaultValues: {
      name: deal.name || '',
      product_id: deal.product_id ?? 0,
      product_variant_id: deal.product_variant_id,
      monthly_volume_override: deal.monthly_volume_override,
      monthly_revenue_override: deal.monthly_revenue_override,
      close_date_override: deal.close_date_override,
      account_owner: deal.account_owner,
      sales_stage: deal.sales_stage,
      notes: '',
      attachments: [],
      origin_campaign_id: deal.origin_campaign,
      apply_to: deal.apply_to,
      door_ids: initialDoorIds,
    },
  })

  const {
    formState: { isValid, isSubmitting },
    setValue,
  } = form

  const onSubmit = form.handleSubmit(async (values) => {
    const body = {
      name: values.name || undefined,
      product_id: values.product_id,
      product_variant_id: values.product_variant_id ?? undefined,
      monthly_volume_override: values.monthly_volume_override || null,
      monthly_revenue_override: values.monthly_revenue_override || null,
      close_date_override: values.close_date_override ?? undefined,
      account_owner: values.account_owner ?? undefined,
      sales_stage: values.sales_stage ?? undefined,
      notes: values.notes ?? undefined,
      attachments: values.attachments ?? undefined,
      origin_campaign_id: values.origin_campaign_id
        ? values.origin_campaign_id.toString()
        : null,
      apply_to: values.apply_to,
      door_ids: values.door_ids,
    }

    await api.editDeal(deal.id, body)
    toast.success('Deal updated successfully')

    onClose()

    await new Promise((resolve) => setTimeout(resolve, 1000))
    if (useInvalidateOnSave) {
      await queryClient.invalidateQueries({ queryKey: dealsTableQueryKey })
    } else {
      await queryClient.refetchQueries({ queryKey: dealsTableQueryKey })
    }
  })

  // Check if the selected product is non-recurring
  const selectedProductId = form.watch('product_id')
  const selectedProduct = products.find((p) => p.id === selectedProductId)
  const isNonRecurring = isNonRecurringProduct(selectedProduct)

  async function handleDelete() {
    if (isDeleting) return

    setIsDeleteConfirmOpen(true)
    const confirmed = await dialogConfirm(
      <>
        Are you sure you want to delete this <b>deal</b>? This action cannot be
        undone.
      </>,
      'Delete Deal?',
      '',
      { confirmLabel: 'Delete' }
    )
    setIsDeleteConfirmOpen(false)

    if (confirmed) {
      setIsDeleting(true)
      try {
        await api.deleteDeal(deal.id)
        toast.success('Deal deleted successfully')
        onClose()
        await Promise.all([
          queryClient.refetchQueries({ queryKey: dealsTableQueryKey }),
          queryClient.invalidateQueries({ queryKey: ['deals'] }),
        ])
      } catch (error) {
        console.error('Failed to delete deal:', error)
        toast.error('Failed to delete deal')
      } finally {
        setIsDeleting(false)
      }
    }
  }

  return (
    <Modal
      onAccept={onSubmit}
      onCancel={onClose}
      title="Edit Deal"
      open={true}
      onOpenChange={(open) => !open && onClose()}
      acceptButtonText="Save"
      size="lg"
      loading={isSubmitting}
      acceptButtonProps={{
        disabled: !isValid,
      }}
      belowFooterChildren={
        <div className="flex justify-center pt-2">
          <FbLink
            onClick={(e) => {
              e.preventDefault()
              if (!isDeleting) {
                void handleDelete()
              }
            }}
            className={`text-danger text-sm ${isDeleting ? 'opacity-50 pointer-events-none' : ''}`}
            to="#"
          >
            {isDeleting ? 'Deleting...' : 'Delete Deal'}
          </FbLink>
        </div>
      }
    >
      {isDeleteConfirmOpen && (
        <div className="absolute inset-0 bg-white/70 z-[60] pointer-events-none" />
      )}
      <div className="space-y-6">
        {/* Deal Name Field - Full Width */}
        <div>
          <TextInput
            control={form.control}
            name="name"
            label="Deal Name"
            placeholder={deal.contact_company?.name || 'Company Name'}
            optional={true}
            maxLength={100}
          />
        </div>

        {/* Product Selection */}
        <div>
          <Select
            label={'Product'}
            control={form.control}
            name={`product_id`}
            options={productOptions}
            placeholder="Select..."
            className="mt-1"
            isClearable={false}
          />
        </div>

        {/* Product Variant Selection */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Product Variant
            <span className="text-gray-500 ml-1">(Optional)</span>
          </label>
          <SingleVariantPicker
            productId={form.watch('product_id') || null}
            selectedVariant={selectedVariant}
            onVariantSelect={(variant) => {
              const fieldName =
                'product_variant_id' as FieldPath<EditDealFormValues>

              if (variant) {
                setValue(fieldName, variant.id, {
                  shouldValidate: true,
                })
                setSelectedVariant(variant)
              } else {
                setValue(fieldName, null, { shouldValidate: true })
                setSelectedVariant(null)
              }
            }}
            buttonText="Select Variant"
            disabled={!form.watch('product_id')}
          />
        </div>

        {/* Sales Stage, Account Owner, and Origin Campaign Row */}
        <div className="grid grid-cols-3 gap-4">
          <div>
            <Select
              label={'Sales Stage'}
              control={form.control}
              name={`sales_stage`}
              options={stageOptions}
              placeholder="Target"
              className="mt-1"
            />
          </div>
          <div>
            <Select
              label={'Account Owner'}
              control={form.control}
              name={`account_owner`}
              options={userOptions}
              placeholder="Select..."
              className="mt-1"
            />
          </div>
          <div>
            {featureFlag.enableCampaigns ? (
              <Select
                label={'Origin Campaign'}
                control={form.control}
                name={`origin_campaign_id`}
                options={campaignOptions}
                placeholder="Select..."
                className="mt-1"
              />
            ) : (
              <div className="h-0" />
            )}
          </div>
        </div>

        {/* Deal Overrides Section - Collapsible */}
        <div className="bg-[#f4f7f5] rounded-lg p-4 mb-4">
          <button
            type="button"
            onClick={() => setOverridesExpanded(!overridesExpanded)}
            className="w-full flex items-center justify-between text-left"
          >
            <div className="flex items-center gap-2">
              <h4 className="text-sm font-medium text-gray-900">
                Deal Overrides
              </h4>
              <span className="bg-[#d5dcd9] text-[#6b7280] text-xs px-2 py-1 rounded-full">
                Optional
              </span>
            </div>
            {overridesExpanded ? (
              <ChevronUp className="h-5 w-5 text-gray-500" />
            ) : (
              <ChevronDown className="h-5 w-5 text-gray-500" />
            )}
          </button>

          {overridesExpanded && (
            <div className="mt-4 space-y-4">
              <div className="grid grid-cols-3 gap-4">
                <TextInput
                  control={form.control}
                  label={
                    isNonRecurring
                      ? 'Est. Volume Override'
                      : 'Est. Monthly Volume'
                  }
                  name={`monthly_volume_override`}
                  type="number"
                  placeholder="2000"
                  optional={true}
                />
                <TextInput
                  control={form.control}
                  label={
                    isNonRecurring
                      ? 'Est. Price Override'
                      : 'Est. Monthly Revenue'
                  }
                  name={`monthly_revenue_override`}
                  type="number"
                  placeholder="$30,000"
                  optional={true}
                  step="0.01"
                />
                <TextInput
                  control={form.control}
                  name={`close_date_override`}
                  label={'Est. Close Date'}
                  type="date"
                  optional={true}
                />
              </div>
            </div>
          )}
        </div>
      </div>
      <SelectDoorsStepWithControl
        control={form.control}
        chainProxyId={deal.contact_company.id}
        companyTotalDoors={companyTotalDoors}
        formTrigger={form.trigger}
      />
    </Modal>
  )
}
